// TerminVorgangDialog.tsx — „Vorgang aus Termin" (Termine-SP8, A17): die
// beiden Aktionen des Peek-Popovers und des Termin-Fensters.
//
// 1. `NeuerVorgangAusTerminDialog`: leitet den Kontakt aus den Teilnehmer-
//    E-Mails ab (terminVorgang.ts; interne Kollegen-Adressen zählen nicht) —
//    genau 1 Treffer ⇒ direkt der zweistufige Erstell-Flow mit vorbefülltem
//    Kontakt; mehrere ⇒ Auswahl-Liste; keiner ⇒ Fallback mit „Kontakt anlegen"
//    (bestehender Anlage-Flow, E-Mail vorbefüllt) bzw. „Kontakt im Vorgang
//    zuweisen" (die Schritt-1-Kaskade des Erstell-Flows IST der Kontakt-
//    Picker). Nach `onCreated` des Flows wird das Ergebnis automatisch an den
//    Termin gebunden (setVorgang UND setTodo — letzteres auch mit `null`,
//    damit kein alter To-do-Slot stehen bleibt).
// 2. `VorgangZuweisenDialog` („Vorgang verknüpfen“): bestehenden Vorgang
//    oder eines seiner To-dos an den Termin binden — seit 27.10.2026 mit der
//    Auswahl aus „Bestehendem Vorgang zuordnen“ (`VorgangMehrfachPicker`,
//    Modus `einzeln`). Hat der Vorgang To-dos, ist die Wahl eines To-dos
//    Pflicht (PO 14.09.2026); E06 ist damit aufgehoben.
//
// Der Kontakt-Dialog (1) ist ein zentriertes Mini-Modal (`cal-vgd-*`, z-index
// 9600 über dem WindowLayer); „Vorgang verknüpfen“ (2) nutzt die Hülle des
// Zuordnungsdialogs im Vorgang-Detail (`vgd-linkdialog-*`, 100000). Beide
// liegen damit auch aus dem Termin-Fenster heraus oben. Das FE
// blendet Schreib-Aktionen nur aus — final sichert das Backend ab
// (`api:write` + Kalender-Leserecht am PATCH …/vorgang bzw. …/todo).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Add16Regular, Dismiss16Regular, PersonAdd16Regular } from "@fluentui/react-icons";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openContactFormWindow } from "../windowManager/ContactFormWindow";
import {
    openVorgangCreateFlow,
    type VorgangCreateResult,
} from "../vorgang/create/VorgangCreateFlowWindow";
import { graphMailService } from "../../services/graphMailService";
import { calendarTerminService } from "../../services/calendarTerminService";
import VorgangPickerAuswahl from "../vorgang/picker/VorgangPickerAuswahl";
import { VorgangMehrfachPicker } from "../vorgang/picker/VorgangMehrfachPicker";
import { listAppUsers, type AppUser } from "../../services/userService";
import { kundeName } from "../../utils/vorgangScope";
import { requestCalendarReload } from "./CalendarStateContext";
import { zeigeGlobalenHinweis } from "./globalerHinweis";
import {
    externeTeilnehmerAdressen,
    interneAdressSet,
    kandidatenAusTreffern,
    type KontaktKandidat,
    type TerminTeilnehmerQuelle,
} from "./terminVorgang";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/** Termin-Bezug der Dialoge: Ziel des Auto-Binds + Teilnehmer-Quelle. */
export interface TerminVorgangKontext extends TerminTeilnehmerQuelle {
    mailbox: string;
    eventId: string;
}

/**
 * Erstell-Ergebnis an den Termin binden (Auto-Bind, A17): IMMER den Vorgang
 * (setVorgang) und — wenn dabei ein To-do entstand — auch dieses (setTodo);
 * beides sind Slots am selben Sidecar. Fehler werden nur geloggt und NIE
 * geworfen — `onCreated` läuft im try des Erstell-Flows; ein Bind-Fehler darf
 * die (bereits erfolgreiche) Anlage nicht als fehlgeschlagen erscheinen lassen.
 */
export type BindErgebnis = { ok: true } | { ok: false; fehler: string };

export async function bindeAnTermin(
    mailbox: string,
    eventId: string,
    result: VorgangCreateResult,
): Promise<BindErgebnis> {
    // K4: IMMER den Vorgang verknüpfen. Wählt der Nutzer im Erstell-Flow einen
    // bestehenden Vorgang, kommt nur eine `todoId` zurück — bisher hing der
    // Termin danach am TODO, erschien im Vorgang nur mit dem Zusatz „über Todo"
    // und ließ sich dort nicht lösen. `parentVorgangId` schließt die Lücke.
    const zielVorgang = result.vorgangId ?? result.parentVorgangId ?? null;
    if (zielVorgang == null) {
        return { ok: false, fehler: "Kein Vorgang zum Verknüpfen gefunden." };
    }
    try {
        await calendarTerminService.setVorgang(mailbox, eventId, zielVorgang);
    } catch (err: any) {
        // K3: NICHT mehr still in die Konsole. Der Aufrufer (TerminLightDialog)
        // hat sich an dieser Stelle oft schon selbst geschlossen — deshalb
        // meldet diese Funktion SELBST über den fensterunabhängigen Kanal.
        console.error("[TerminVorgangDialog] Auto-Bind an den Termin fehlgeschlagen", err);
        const fehler =
            "Der Vorgang wurde angelegt, die Verknüpfung mit dem Termin ist aber fehlgeschlagen.";
        zeigeGlobalenHinweis(fehler, { art: "fehler" });
        return { ok: false, fehler };
    }
    // Das To-do MIT binden (PO 14.09.2026: „einem To-do oder neues To-do").
    // Bis dahin wurde es hier bewusst fallengelassen (E06). ERST nach dem
    // Vorgang: der Service prüft, dass das To-do zum Vorgang des Sidecars
    // gehört. Scheitert nur dieser zweite Schritt, hängt der Termin trotzdem
    // am Vorgang — das ist gesagt, nicht verschwiegen, aber kein Fehlschlag
    // der Anlage.
    try {
        // Auch `null` explizit schreiben: Wird aus einem bereits verknüpften
        // Termin ein neuer Vorgang ohne To-do angelegt, darf die alte todoId
        // nicht am Sidecar und damit am alten Vorgang hängen bleiben.
        await calendarTerminService.setTodo(mailbox, eventId, result.todoId ?? null);
    } catch (err: any) {
        console.error("[TerminVorgangDialog] To-do-Verknüpfung fehlgeschlagen", err);
        zeigeGlobalenHinweis(
            "Der Termin hängt am Vorgang, die Zuordnung zum To-do ist aber fehlgeschlagen.",
            { art: "fehler" },
        );
    }
    requestCalendarReload();
    return { ok: true };
}

/** Escape schließt den offenen Dialog (Muster TerminPeekPopover). */
function useEscape(aktiv: boolean, onClose: () => void) {
    useEffect(() => {
        if (!aktiv) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [aktiv, onClose]);
}

/** Gemeinsame Modal-Hülle (Overlay + Karte); Klick auf das Overlay schließt. */
const ModalHuelle: React.FC<{
    titel: string;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ titel, onClose, children }) => {
    return createPortal(
        <div
            className="cal-rd cal-vgd-overlay"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="cal-vgd" role="dialog" aria-label={titel}>
                <div className="cal-vgd-head">
                    <h3>{titel}</h3>
                    <button
                        type="button"
                        className="cal-peek-x"
                        aria-label="Schließen"
                        onClick={onClose}
                    >
                        <Dismiss16Regular />
                    </button>
                </div>
                {children}
            </div>
        </div>,
        document.body,
    );
};

type ResolveStand =
    | { phase: "laden" }
    | { phase: "auswahl"; kandidaten: KontaktKandidat[] }
    | { phase: "fallback"; unbekannt: string[] };

export interface NeuerVorgangAusTerminDialogProps {
    /** null = geschlossen; sonst startet der Kontakt-Resolve sofort. */
    kontext: TerminVorgangKontext | null;
    onClose: () => void;
    /** Nach erfolgreichem Auto-Bind (z. B. Sidecar-Verknüpfung nachziehen). */
    onGebunden?: (result: VorgangCreateResult) => void;
}

export const NeuerVorgangAusTerminDialog: React.FC<NeuerVorgangAusTerminDialogProps> = ({
    kontext,
    onClose,
    onGebunden,
}) => {
    const { openWindow } = useWindowManager();
    const [stand, setStand] = useState<ResolveStand>({ phase: "laden" });
    useEscape(kontext != null, onClose);

    /** Erstell-Flow öffnen (mit/ohne vorbefülltem Kontakt) + Auto-Bind. */
    const openFlow = useCallback(
        (k: KontaktKandidat | null) => {
            if (!kontext) return;
            const { mailbox, eventId } = kontext;
            onClose();
            openVorgangCreateFlow(openWindow, {
                initialContactId: k?.contactId ?? null,
                initialContactName: k?.displayName ?? null,
                onCreated: async (result) => {
                    const ergebnis = await bindeAnTermin(mailbox, eventId, result);
                    if (ergebnis.ok) onGebunden?.(result);
                },
            });
        },
        [kontext, onClose, onGebunden, openWindow],
    );
    // Ref-Brücke für den Resolve-Effekt: der Effekt soll NUR bei Kontext-
    // Wechsel laufen, nicht wenn sich Callback-Identitäten ändern.
    const openFlowRef = useRef(openFlow);
    openFlowRef.current = openFlow;

    // Kontakt-Resolve beim Öffnen: Teilnehmer → externe Adressen → exakte
    // Treffer der Empfänger-Kontaktsuche (RBAC-/mandanten-gescoped im Backend).
    useEffect(() => {
        if (!kontext) return;
        let cancelled = false;
        setStand({ phase: "laden" });
        (async () => {
            const users = await listAppUsers().catch(() => [] as AppUser[]);
            const intern = interneAdressSet(users, kontext.mailbox);
            const adressen = externeTeilnehmerAdressen(kontext, intern);
            if (adressen.length === 0) {
                // Rein interner Termin: direkt der Flow — den Kontakt wählt der
                // User in Schritt 1 (Zuweisungs-Kaskade).
                if (!cancelled) openFlowRef.current(null);
                return;
            }
            const paare = await Promise.all(
                adressen.map(async (email) => ({
                    email,
                    hits: await graphMailService.searchEmailRecipients(email),
                })),
            );
            if (cancelled) return;
            const { kandidaten, unbekannt } = kandidatenAusTreffern(paare);
            if (kandidaten.length === 1) {
                openFlowRef.current(kandidaten[0]);
            } else if (kandidaten.length > 1) {
                setStand({ phase: "auswahl", kandidaten });
            } else {
                setStand({ phase: "fallback", unbekannt });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [kontext]);

    /**
     * Unbekannte Adresse → bestehender Anlage-Flow (Privatkontakt-Fenster) mit
     * vorbefüllter E-Mail; nach der Anlage geht es nahtlos in den Erstell-Flow
     * mit dem neuen Kontakt weiter (statt der Standard-Navigation zur Akte).
     */
    const neuerKontakt = useCallback(
        (email: string) => {
            if (!kontext) return;
            const { mailbox, eventId } = kontext;
            onClose();
            openContactFormWindow(openWindow, {
                kind: "private",
                initialValues: { emailPrivate: email },
                onCreated: (contactId) => {
                    openVorgangCreateFlow(openWindow, {
                        initialContactId: contactId,
                        onCreated: async (result) => {
                            const ergebnis = await bindeAnTermin(mailbox, eventId, result);
                            if (ergebnis.ok) onGebunden?.(result);
                        },
                    });
                },
            });
        },
        [kontext, onClose, onGebunden, openWindow],
    );

    if (!kontext) return null;
    return (
        <ModalHuelle titel="Neuer Vorgang aus Termin" onClose={onClose}>
            {stand.phase === "laden" && (
                <div className="cal-vgd-hint">
                    Teilnehmer werden mit den Kontakten abgeglichen…
                </div>
            )}
            {stand.phase === "auswahl" && (
                <>
                    <div className="cal-vgd-hint">
                        Mehrere passende Kontakte gefunden — wen betrifft der Vorgang?
                    </div>
                    <div className="cal-vgd-liste">
                        {stand.kandidaten.map((k) => (
                            <button
                                key={k.contactId}
                                type="button"
                                className="cal-vgd-row"
                                onClick={() => openFlow(k)}
                            >
                                <span className="cal-vgd-row-name">{k.displayName}</span>
                                <span className="cal-vgd-row-sub">
                                    {k.email}
                                    {k.companyName ? ` · ${k.companyName}` : ""}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="cal-vgd-foot">
                        <button
                            type="button"
                            className="cal-peek-btn"
                            onClick={() => openFlow(null)}
                        >
                            Anderen Kontakt zuweisen
                        </button>
                    </div>
                </>
            )}
            {stand.phase === "fallback" && (
                <>
                    <div className="cal-vgd-hint">
                        Zu den Teilnehmer-E-Mails wurde kein Kontakt gefunden.
                    </div>
                    <div className="cal-vgd-liste">
                        {stand.unbekannt.map((email) => (
                            <div key={email} className="cal-vgd-row cal-vgd-row-static">
                                <span className="cal-vgd-row-name">{email}</span>
                                <button
                                    type="button"
                                    className="cal-peek-btn"
                                    onClick={() => neuerKontakt(email)}
                                >
                                    <PersonAdd16Regular aria-hidden /> Kontakt anlegen
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="cal-vgd-foot">
                        {/* Schritt 1 des Erstell-Flows (Zuweisungs-Kaskade) IST der
                            Kontakt-Picker — Auto-Bind bleibt erhalten. */}
                        <button
                            type="button"
                            className="cal-peek-btn cal-peek-btn-primary"
                            onClick={() => openFlow(null)}
                        >
                            <Add16Regular aria-hidden /> Kontakt im Vorgang zuweisen
                        </button>
                    </div>
                </>
            )}
        </ModalHuelle>
    );
};

/**
 * Suchbare Vorgangs-Liste des Termin-Kontexts.
 *
 * K10 (23.08.2026): Der Rumpf ist jetzt der GESTUFTE, serverseitig gesuchte und
 * paginierte Picker aus Cluster VORGANG. Vorher lud diese Komponente über
 * `getAllVorgaenge()` ALLE Vorgänge des Mandanten, filterte im Browser und
 * schnitt auf 50 Zeilen — langsam und, schlimmer, unvollständig. Der Name
 * bleibt, damit die beiden Aufrufer (Zuweisen-Dialog und TerminFormWindow)
 * unverändert bleiben; sie bekommen weiterhin ein Objekt mit `id` und `titel`.
 *
 * Trägt bewusst KEINE Bind-Logik — der Aufrufer entscheidet, was mit dem
 * gewählten Vorgang passiert. `disabled` sperrt die Zeilen, solange gebunden wird.
 */
export const VorgangSuchListe: React.FC<{
    onPick: (v: { id: number; titel: string }) => void;
    disabled?: boolean;
    autoFocus?: boolean;
    /** Steuert die Rangstufe 1 („Vorgänge dieses Kontakts"). */
    contactId?: number | null;
    /** Markiert den aktuell verknüpften Vorgang in der Liste. */
    aktuelleVorgangId?: number | null;
}> = ({ onPick, disabled = false, autoFocus = true, contactId = null, aktuelleVorgangId = null }) => (
    <VorgangPickerAuswahl
        onPick={(v) => onPick({ id: v.id, titel: v.titel })}
        contactId={contactId}
        disabled={disabled}
        autoFocus={autoFocus}
        aktuelleVorgangId={aktuelleVorgangId}
    />
);

export interface VorgangZuweisenDialogProps {
    /** null = geschlossen. `contactId` steuert nur die Rangfolge im Picker. */
    kontext: { mailbox: string; eventId: string; contactId?: number | null } | null;
    onClose: () => void;
    /**
     * Nach erfolgreichem Binden (z. B. Sidecar-Anzeige nachziehen). `todoId`
     * ist null, wenn der Termin direkt am Vorgang hängt (Vorgang ohne To-dos).
     */
    onGebunden?: (vorgangId: number, todoId: number | null) => void;
}

/**
 * Das EINE Ziel einer `einzeln`-Auswahl: `t:<todo>` (Wert = Eltern-Vorgang)
 * oder `v:<vorgang>`. Neue To-dos (`n:`) hat der Picker vor dem Übernehmen
 * schon angelegt und als `t:` eingetragen.
 */
export function terminZiel(
    auswahl: ReadonlyMap<string, number>,
): { vorgangId: number; todoId: number | null } | null {
    if (auswahl.size !== 1) return null;
    const [[key, vorgangId]] = Array.from(auswahl.entries());
    const id = Number(key.slice(2));
    if (!Number.isInteger(id) || id <= 0) return null;
    if (key.startsWith("t:")) return { vorgangId, todoId: id };
    if (key.startsWith("v:")) return { vorgangId: id, todoId: null };
    return null;
}

/**
 * „Vorgang verknüpfen“: bestehenden Vorgang oder eines seiner To-dos an den
 * Termin binden (A17).
 *
 * ══ DASSELBE POPUP WIE „BESTEHENDEM VORGANG ZUORDNEN“ (User 27.10.2026) ═════
 * „Vorgang verknüpfen: als Popup natürlich über dem normalen Popup, also nicht
 * dahinter, und das Popup ‚Bestehendem Vorgang zuordnen‘ nutzen, nur dann
 * anderer Titel.“ Der Rumpf ist deshalb `VorgangMehrfachPicker` — die Auswahl
 * der Mail und der Telefonie — im Modus `einzeln`, denn ein Termin hängt an
 * genau einem Ziel. Vorher war es ein eigener Zwei-Schritt-Dialog
 * („Zu Vorgang hinzufügen“, erst Vorgang, dann To-do).
 *
 * Die Regel bleibt (PO 14.09.2026): Ein Vorgang MIT To-dos ist kein Ziel —
 * aufklappen, To-do wählen oder „Neues To-do…“; ohne To-dos ist der Vorgang
 * selbst das Ziel.
 *
 * Die Hülle ist der Zuordnungsdialog des Vorgang-Details (`vgd-linkdialog-*`,
 * z-index 100000 = `Z_LAYERS.ueberFenster`): über JEDEM Fenster, auch über dem
 * Termin-Fenster, aus dem er aufgeht. Ein WindowManager-Fenster wie in der Mail
 * ginge hier nicht — ein Klick ins Termin-Fenster holte das nach vorn und
 * begrübe die Auswahl dahinter.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const VorgangZuweisenDialog: React.FC<VorgangZuweisenDialogProps> = ({
    kontext,
    onClose,
    onGebunden,
}) => {
    useEscape(kontext != null, onClose);

    /** Vorgang + To-do am Sidecar setzen. Wirft — der Picker zeigt den Fehler. */
    const binden = useCallback(
        async (ziel: { vorgangId: number; todoId: number | null }) => {
            if (!kontext) return;
            await calendarTerminService.setVorgang(kontext.mailbox, kontext.eventId, ziel.vorgangId);
            // Immer BEIDE Slots setzen. `null` ist hier fachlich wichtig:
            // Beim Wechsel von Vorgang A/To-do A zu einem Vorgang ohne
            // To-dos darf die alte todoId nicht am Sidecar stehen bleiben.
            await calendarTerminService.setTodo(kontext.mailbox, kontext.eventId, ziel.todoId);
            onGebunden?.(ziel.vorgangId, ziel.todoId);
            // K2: der Terminblock im Raster trägt danach das Vorgangs-Symbol —
            // dafür muss die Sidecar-Meta neu gelesen werden.
            requestCalendarReload();
            zeigeGlobalenHinweis(
                ziel.todoId != null
                    ? "Termin mit dem To-do des Vorgangs verknüpft."
                    : "Termin mit dem Vorgang verknüpft.",
            );
            onClose();
        },
        [kontext, onGebunden, onClose],
    );

    const fehlerText = useCallback((err: unknown, art: "anlegen" | "zuordnen") => {
        console.error("[VorgangZuweisenDialog] Verknüpfen fehlgeschlagen", err);
        if (art === "anlegen") return "To-do konnte nicht angelegt werden.";
        const text = String((err as { message?: unknown } | null)?.message ?? "");
        return text.includes("403") ? "Kein Zugriff auf den Ziel-Vorgang." : "Vorgang konnte nicht verknüpft werden.";
    }, []);

    if (!kontext) return null;
    return createPortal(
        <div className="vgd vgd-linkdialog-backdrop" role="presentation" onPointerDown={onClose}>
            <div
                className="vgd-linkdialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="termin-vorgang-verknuepfen-titel"
                // Maße des Mail-Fensters „Bestehendem Vorgang zuordnen“ (560 × 600).
                style={{ width: "min(560px, 100%)", height: "min(640px, calc(100vh - 48px))" }}
                onPointerDown={(e) => e.stopPropagation()}
            >
                <div className="vgd-linkdialog-head">
                    <div>
                        <div className="vgd-linkdialog-eyebrow">Termin</div>
                        <h2 id="termin-vorgang-verknuepfen-titel">Vorgang verknüpfen</h2>
                    </div>
                    <button type="button" className="vgd-linkdialog-close" onClick={onClose} aria-label="Schließen">
                        ×
                    </button>
                </div>
                <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <VorgangMehrfachPicker
                        einzeln
                        contactId={kontext.contactId ?? null}
                        onAbbrechen={onClose}
                        fehlerText={fehlerText}
                        knopfText={() => "Verknüpfen"}
                        onUebernehmen={async ({ auswahl }) => {
                            const ziel = terminZiel(auswahl);
                            if (ziel) await binden(ziel);
                        }}
                    />
                </div>
            </div>
        </div>,
        document.body,
    );
};
